import * as React from "react";
import "./ProvisionProgressDlg.scss";
import Api from "./Api";
import Dialog from "./input/Dialog";
import { IProvision } from "@testout/testout-commerce/models/Provision";
import { EmailArray } from "@testout/testout-commerce/models/Email";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { User } from "@testout/testout-commerce/models/labsim/User";
import { Reservation, ReservationType } from "@testout/testout-commerce/models/labsim/Reservation";
import { Contact } from "@testout/testout-commerce/models/hubspot/Contact";
import { ContactName } from "@testout/testout-commerce/models/ContactName";

interface ProvisionProgressProps {
    provision: IProvision;
    products: ListItem[],
    isOpen: boolean;
    onClose: (failed: boolean) => void;
}

interface ProvisionProgressState {
    percent: number;
    messages: string[];
    disableClose: boolean;
}

export default class ProvisionProgressDlg extends React.PureComponent<ProvisionProgressProps, ProvisionProgressState> {
    constructor(props: ProvisionProgressProps) {
        super(props);
        this.state = { messages: [], percent: 0, disableClose: true };
    }

    _scrollerRef = React.createRef<HTMLDivElement>();

    onOpen = async () => {
        const { provision, products } = this.props;

        try {
            this.setState({ messages: [], percent: 0, disableClose: true });

            // Determine the task size for progress bar
            const counts = provision.companies.map(company => new EmailArray(company.emails).length);
            const taskPercent = 100 / (1 + counts.length + counts.reduce((sum, count) => sum + count, 0) * 10);
            let percent = 0;

            // Build the list of courses from the selected products
            const productIds: number[] = provision.products.map((productNumber: string) => parseInt(products.find((item) => item.value === productNumber)?.["data"]?.["courseId"] ?? "0" as string));

            // Provision each company
            for (const company of provision.companies) {

                // COMPANY
                this.setState({ messages: [...this.state.messages, `Associating ${company.name} to LabSim campus.`] });

                // Ensure the company is linked to the selected campus
                await Api.patch(`/api/companies/${company.companyId}`, { labsim6_companyprofileid: company.campusProfileId });
                this.setState({ percent: percent += taskPercent });

                // INSTRUCTOR LICENSE
                if (provision.useInstructorLicense) {
                    this.setState({ messages: [...this.state.messages, ` Provisioning instructor licenses...`] });
                    const reservations = await Api.get<Reservation[]>(`/api/campuses/${company.campusProfileId}/reservations?reservationType=InstructorLicense&description=Instructor License`);
                    const reservation = reservations.find(r => !r.isDeleted && r.name === "Instructor License");
                    if (reservation) {
                        // Merge the selected courses with the existing ones and extend the expiration date if needed
                        reservation.productIds = [...new Set([...reservation.productIds, ...productIds])];
                        if (provision.expirationDate && reservation.expirationDate && reservation.expirationDate < provision.expirationDate)
                            reservation.expirationDate = provision.expirationDate;
                        await Api.put(`/api/reservations`, reservation);
                    } else
                        await Api.post<Reservation>(`/api/reservations`,
                            {
                                reservationType: ReservationType.InstructorLicense,
                                productIds: productIds,
                                expirationDate: provision.expirationDate,
                                isCampusRequired: true,
                                campusId: company.campusProfileId,
                                name: "Instructor License",
                                allowedActivationCount: 0
                            });
                }
                this.setState({ percent: percent += taskPercent });

                // Provision each email of the company
                for (const email of new EmailArray(company.emails)) {

                    try {

                        // CONTACT
                        this.setState({
                            messages: [...this.state.messages, `Setting up instructor ${email.toString()}.`]
                        });

                        // Try to find the contact by email address
                        let contact = await Api.get<Contact>(`/api/contacts?email=${encodeURIComponent(email.address)}`);
                        this.setState({ percent: percent += taskPercent });

                        // Determine a first name and last name from the email (LabSim requires both parts)
                        let person = new ContactName(email.displayName || email.localPart);
                        if (!person.firstNames)
                            person.firstNames = (person.lastName?.length ?? 0) > 0 ? person.lastName?.[0] : "unknown";
                        if (!person.lastName)
                            person.lastName = "unknown"

                        // Create a new contact if we could not find one and link it to the company
                        if (!contact)
                            contact = await Api.post<Contact>(`/api/contacts`,
                                {
                                    email: email.address,
                                    fullname: person.toString(),
                                    roles: "instructor",
                                    associatedcompanyid: company.companyId
                                }) as Contact;
                        this.setState({ percent: percent += taskPercent });

                        // Ensure the contact is linked to the deal if one was provided
                        if (provision.dealId)
                            await Api.put(`/api/deals/${provision.dealId}/contacts/${contact.hs_object_id}`, "PUT");
                        this.setState({ percent: percent += taskPercent });


                        // USER
                        this.setState({ messages: [...this.state.messages, ` Provisioning LabSim user...`] });

                        // Try to locate an existing user for the contact
                        let user: User | undefined;
                        if (contact.labsim_6_userprofileid) {
                            user = await Api.get<User>(`/api/users/${contact.labsim_6_userprofileid}`);
                            if (!user) contact.labsim_6_userprofileid = "";
                        }
                        this.setState({ percent: percent += taskPercent });

                        // Create a new user if we could not find one and link it to the contact
                        if (!contact.labsim_6_userprofileid) {
                            user = await Api.post<User>("/api/users",
                                {
                                    givenName:"",
                                    surname:"",
                                    signOns: [{ systemId: 0, email: email.address }],
                                    fullName: person.toString()
                                });
                            this.setState({ percent: percent += taskPercent });

                            if (!user)
                                throw new Error("User could not be created");
                            await Api.patch(`/api/contacts/${contact.hs_object_id}`,
                                { labsim_6_userprofileid: user.userProfileId });
                            this.setState({ percent: percent += taskPercent });
                        } else
                            this.setState({ percent: percent += 2 * taskPercent });

                        if (!user)
                            throw new Error("User could not be found or created");

                        // Ensure the user is linked to the selected campus
                        await Api.post(`/api/users/${user.userProfileId}/campuses/${company.campusProfileId}?isInstructor=true&isAdmin=${company.makeTeachersAdmins}`);
                        this.setState({ percent: percent += taskPercent });


                        // ACCESS

                        // Make a reservation and activate it for the user
                        let reservation: Reservation | undefined;
                        if (!provision.useInstructorLicense) {
                            this.setState({ messages: [...this.state.messages, ` Provisioning LabSim course access...`] });
                            reservation = await Api.post<Reservation>(`/api/reservations`,
                                {
                                    reservationType: ReservationType.ActivationCode,
                                    productIds: productIds,
                                    expirationDate: provision.expirationDate,
                                    isCampusRequired: true,
                                    campusId: company.campusProfileId,
                                    allowedActivationCount: 1
                                });
                            this.setState({ percent: percent += taskPercent });

                            await Api.post(`/api/users/${user.userProfileId}/activations/${reservation.reservationId}/1`);
                            this.setState({ percent: percent += taskPercent });
                        } else
                            this.setState({ percent: percent += 2 * taskPercent });


                        // Send a welcome email to the contact with their temporary credentials
                        this.setState({ messages: [...this.state.messages, ` Sending welcome email...`] });
                        const productList = provision.products.map(productNumber =>
                            products.find(item => item.value === productNumber)?.["name"] ?? "");

                        await Api.post(`/api/contacts/${contact.hs_object_id}/welcome`,
                            {
                                userProfileId: user.userProfileId,
                                campusProfileId: company.campusProfileId,
                                reservationId: reservation?.reservationId,
                                companyId: company.companyId,
                                email: contact.email,
                                firstname: contact.firstname,
                                lastname: contact.lastname,
                                login: (user.signOns ?? []).find(signOn => signOn.systemId === "0")?.["userKey"],
                                password: (user.signOns ?? []).find(signOn => signOn.systemId === "0")?.["tempPassword"],
                                productlist: this.formatList(productList)
                            });
                        this.setState({ percent: percent += taskPercent });

                    }
                    catch (error: any) {
                        this.setState({ messages: [...this.state.messages, `*${error.message}`] });
                    }
                }
            }
            this.setState({ disableClose: false, messages: [...this.state.messages, `Provisioning complete.`] });
        }
        catch (err: any) {
            this.setState({ disableClose: false, messages: [...this.state.messages, `*${err.message}`] });
        }
    }

    onClose = () => {
        this.props.onClose(!!this.state.messages.find(message => message.startsWith("*")));
    }
    
    private formatList(n: string[]): string {
        return n.length === 0 ? '' : n.length === 1 ? n[0] 
            : n.length === 2 ? n.join(" and ") : `${n.slice(0, -1).join(", ")}, and ${n.slice(-1)}`;
    }

    scrollToBottom() {
        if (this.props.isOpen && this._scrollerRef.current) {
            const divScroller = this._scrollerRef.current as HTMLDivElement;
            divScroller.scrollTop = divScroller.scrollHeight;
        }
    }

    componentDidMount() {
        this.scrollToBottom();
    }

    componentDidUpdate() {
        this.scrollToBottom();
    }

    render() {
        return <Dialog title="Provision Progress" className="provision-dialog" isOpen={this.props.isOpen}
                       onOpen={this.onOpen} disableClose={this.state.disableClose} onClose={this.onClose}>
                   <div className="progress-bar">
                       <div style={{ width: `${this.state.percent}%` }}></div></div>
                   <div className="messages scroller" ref={this._scrollerRef}>
                       <ul>
                           {this.state.messages.map(
                               (message, i) => <li key={i} className={message.startsWith(" ")
                                   ? "indent"
                                   : message.startsWith("*")
                                   ? "error"
                                   : ""}>{message.startsWith("*") ? message.substr(1) : message}</li>)}
                       </ul>
                   </div>
                   <footer>
                       <button className="primary" disabled={this.state.disableClose}
                               onClick={this.onClose}>Close</button></footer>
               </Dialog>;
    }
}